import { useRecordContext } from "ra-core";

import type { Contact } from "../types";
import {
  useContactOutreachTimeline,
  type OutreachTimelineEntry,
} from "./useContactOutreachTimeline";

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const KIND_LABEL: Record<OutreachTimelineEntry["kind"], string> = {
  sent: "Sent",
  reply: "Replied",
  opt_out: "Opted out",
  unsubscribe: "Unsubscribed",
};

const TimelineRow = ({ entry }: { entry: OutreachTimelineEntry }) => (
  <div className="flex flex-col gap-0.5 border-b py-2 text-sm last:border-b-0">
    <div className="flex items-center justify-between gap-2">
      <span className="font-medium">
        {KIND_LABEL[entry.kind]} — {entry.title}
      </span>
      <span className="text-muted-foreground shrink-0 text-xs">
        {fmtDateTime(entry.occurredAt)}
      </span>
    </div>
    {entry.detail && (
      <span className="text-muted-foreground text-xs">{entry.detail}</span>
    )}
    {entry.sendingIdentity && (
      <span className="text-muted-foreground text-xs">
        via {entry.sendingIdentity}
      </span>
    )}
  </div>
);

const OutreachTimeline = ({ contactId }: { contactId: number }) => {
  const { data: entries, isPending, isError } =
    useContactOutreachTimeline(contactId);

  if (isPending) return null;

  if (isError) {
    return (
      <p className="text-muted-foreground text-xs italic">
        Couldn't load outreach history.
      </p>
    );
  }

  if (!entries || entries.length === 0) {
    return (
      <p className="text-muted-foreground text-xs italic">
        No outreach activity yet.
      </p>
    );
  }

  return (
    <div className="mt-1 flex flex-col">
      {entries.map((entry) => (
        <TimelineRow key={entry.id} entry={entry} />
      ))}
    </div>
  );
};

/**
 * Outreach section of ContactShow: a merged, read-only send/reply/bounce/
 * opt-out timeline (queried directly against the mentorax schema — see
 * useContactOutreachTimeline). DR-20260830-01 granted `authenticated`
 * read-only SELECT on the 5 tables the timeline needs; verified live via
 * information_schema before this was built.
 *
 * Current list membership (what this panel used to also show, read/write,
 * as a single "List/Identity/Added by/Added + Remove" block) moved out
 * 2026-09-08 to two dedicated dropdowns — ContactOutreachAxisSelector, one
 * for Offer and one for Channel — placed in the aside below Lead Source,
 * same pattern as Status. See backlog.md's "Design pattern — Offer vs.
 * Channel are two independent axes": a contact can now be active in one
 * list PER AXIS, which a single combined block could no longer represent.
 */
export const ContactOutreachPanel = () => {
  const record = useRecordContext<Contact>();
  if (!record) return null;

  return <OutreachTimeline contactId={Number(record.id)} />;
};
